import { WORKSPACE_ID } from './constants';

export const normalizeSnippetRecord = (snippet = {}) => ({
  workspaceId: snippet.workspaceId || WORKSPACE_ID,
  id: String(snippet.id || ''),
  title: String(snippet.title || ''),
  type: snippet.type || 'plain',
  content: String(snippet.content || ''),
  createdAt: Number(snippet.createdAt || 0),
  updatedAt: Number(snippet.updatedAt || snippet.createdAt || 0),
  lastUsedAt: Number(snippet.lastUsedAt || snippet.createdAt || 0),
  usageCount: Number(snippet.usageCount || 0),
  isDeleted: Boolean(snippet.isDeleted),
  isPermanent: Boolean(snippet.isPermanent),
  isPinned: Boolean(snippet.isPinned),
  ownerUserId: String(snippet.ownerUserId || '')
});

export const loadDirtyIds = (storageKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

export const persistDirtyIds = (storageKey, ids) => {
  const unique = [...new Set((ids || []).filter(Boolean))];
  localStorage.setItem(storageKey, JSON.stringify(unique));
  return unique;
};

export const mergeRemoteSnapshot = (localSnippets = [], remoteSnippets = [], dirtyIds = []) => {
  const local = localSnippets.map(normalizeSnippetRecord).filter(s => s.id);
  const remote = remoteSnippets.map(normalizeSnippetRecord).filter(s => s.id);
  const localMap = new Map(local.map(s => [s.id, s]));
  const remoteMap = new Map(remote.map(s => [s.id, s]));
  const dirty = new Set(dirtyIds || []);
  const order = [
    ...local.map(s => s.id),
    ...remote.map(s => s.id).filter(id => !localMap.has(id))
  ];

  const merged = [];
  for (const id of order) {
    const localItem = localMap.get(id);
    const remoteItem = remoteMap.get(id);

    if (localItem && !remoteItem) {
      merged.push(localItem);
      dirty.add(id);
      continue;
    }

    if (!localItem && remoteItem) {
      merged.push(remoteItem);
      continue;
    }

    if (!localItem || !remoteItem) continue;

    const localUpdated = Number(localItem.updatedAt || 0);
    const remoteUpdated = Number(remoteItem.updatedAt || 0);

    if (dirty.has(id) && localUpdated >= remoteUpdated) {
      merged.push(localItem);
      continue;
    }

    if (remoteUpdated > localUpdated) {
      merged.push(remoteItem);
      dirty.delete(id);
      continue;
    }

    if (localUpdated > remoteUpdated) {
      merged.push(localItem);
      dirty.add(id);
      continue;
    }

    // Equal versions: keep local ordering/state, but fill any schema fields
    // that may only exist in the server snapshot.
    merged.push({ ...remoteItem, ...localItem });
  }

  return { snippets: merged, dirtyIds: [...dirty] };
};